<template>
  <div class="hmda-page">
    <div class="page-header">
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-folder-magnifying-glass"></i>
        </div>

        <div>
          <h1 class="page-title">AI Insuring — Closing Packages</h1>
          <p class="page-subtitle">Parsed FHA closing packages — what was found and filed to AI Insuring</p>
        </div>
      </div>

      <div class="header-actions">
        <select v-model="timeFrame" class="filter-select days-select" @change="loadData">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="last_90_days">Last 90 Days</option>
        </select>

        <button class="refresh-btn" @click="loadData" :disabled="loading">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-sections">
      <div class="summary-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-list-check section-icon"></i>
            <span class="section-title">Queue Overview</span>
          </div>
        </div>

        <div class="section-cards">
          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-table-rows summary-icon"></i>
              <span class="summary-label">Total Runs</span>
            </div>
            <div class="summary-value">{{ summary.total }}</div>
          </div>
        </div>
      </div>

      <div class="summary-section decisions-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-sparkles section-icon"></i>
            <span class="section-title">Results</span>
          </div>
        </div>

        <div class="section-cards results-cards">
          <div class="summary-card approve">
            <div class="summary-card-header">
              <i class="fa-duotone fa-circle-check summary-icon"></i>
              <span class="summary-label">Processed</span>
            </div>
            <div class="summary-value">{{ summary.processed }}</div>
          </div>

          <div class="summary-card request-only">
            <div class="summary-card-header">
              <i class="fa-duotone fa-folder-xmark summary-icon"></i>
              <span class="summary-label">No Package</span>
            </div>
            <div class="summary-value">{{ summary.noPackage }}</div>
          </div>

          <div class="summary-card manual-review">
            <div class="summary-card-header">
              <i class="fa-duotone fa-triangle-exclamation summary-icon"></i>
              <span class="summary-label">Failed</span>
            </div>
            <div class="summary-value">{{ summary.failed }}</div>
          </div>

          <div class="summary-card no-audit">
            <div class="summary-card-header">
              <i class="fa-duotone fa-file-circle-question summary-icon"></i>
              <span class="summary-label">Missing Docs</span>
            </div>
            <div class="summary-value">{{ summary.withMissing }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="toolbar single-line-toolbar">
      <input
        v-model.trim="search"
        type="text"
        class="search-input"
        placeholder="Search loan #, borrower, LO, status, found/missing docs, summary..."
      />

      <select v-model="statusFilter" class="filter-select toolbar-select">
        <option value="">All Statuses</option>
        <option value="processed">Processed</option>
        <option value="no_package">No Package</option>
        <option value="failed">Failed</option>
      </select>

      <select v-model="dryRunFilter" class="filter-select toolbar-select">
        <option value="">Live + Dry Runs</option>
        <option value="live">Live Only</option>
        <option value="dry">Dry Runs Only</option>
      </select>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div class="table-card-header">
        <div class="table-card-title-wrap">
          <i class="fa-duotone fa-clipboard-list-check table-card-icon"></i>
          <span class="table-card-title">AI Insuring Queue</span>
        </div>

        <div class="table-card-count">
          {{ filteredRows.length }}
        </div>
      </div>

      <div v-if="loading" class="table-state">Loading AI Insuring log...</div>
      <div v-else-if="errorMessage" class="table-state error">{{ errorMessage }}</div>
      <div v-else-if="pagedRows.length === 0" class="table-state">No AI Insuring records found.</div>

      <div v-else class="table-wrap">
        <table class="locks-table">
          <thead>
            <tr>
              <th @click="setSort('processedatutc')">
                Processed
                <span>{{ sortIcon('processedatutc') }}</span>
              </th>
              <th @click="setSort('loannumber')">
                Loan #
                <span>{{ sortIcon('loannumber') }}</span>
              </th>
              <th @click="setSort('borrowername')">
                Borrower
                <span>{{ sortIcon('borrowername') }}</span>
              </th>
              <th @click="setSort('loanofficername')">
                LO
                <span>{{ sortIcon('loanofficername') }}</span>
              </th>
              <th @click="setSort('status')">
                Status
                <span>{{ sortIcon('status') }}</span>
              </th>
              <th @click="setSort('foundcount')">
                Found
                <span>{{ sortIcon('foundcount') }}</span>
              </th>
              <th @click="setSort('notfoundcount')">
                Missing
                <span>{{ sortIcon('notfoundcount') }}</span>
              </th>
              <th @click="setSort('pagesscanned')">
                Pages
                <span>{{ sortIcon('pagesscanned') }}</span>
              </th>
              <th @click="setSort('dryrun')">
                Run
                <span>{{ sortIcon('dryrun') }}</span>
              </th>
              <th>Summary</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in pagedRows" :key="row.id">
              <td>{{ formatDateTime(row.processedatutc) }}</td>

              <td>
                <div class="primary-text">{{ row.loannumber || '—' }}</div>
              </td>

              <td>{{ borrowerName(row) }}</td>
              <td class="compact-cell">{{ row.loanofficername || '—' }}</td>

              <td>
                <span
                  class="decision-pill"
                  :class="statusClass(row.status)"
                >
                  {{ statusLabel(row.status) }}
                </span>
              </td>

              <td class="compact-cell">
                <span class="count-good" :title="docListTitle(row.foundlist)">{{ row.foundcount ?? 0 }}</span>
              </td>
              <td class="compact-cell">
                <span
                  :class="(row.notfoundcount ?? 0) > 0 ? 'count-bad' : 'count-zero'"
                  :title="docListTitle(row.missinglist)"
                >{{ row.notfoundcount ?? 0 }}</span>
              </td>

              <td class="compact-cell">{{ row.pagesscanned ?? 0 }}</td>

              <td class="compact-cell">{{ row.dryrun ? 'Dry Run' : 'Live' }}</td>

              <td>
                <button
                  class="summary-btn"
                  :disabled="!row.summary && !row.error && !row.foundlist && !row.missinglist"
                  @click="openSummaryModal(row)"
                >
                  Summary
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredRows.length" class="pagination-bar">
      <div class="pagination-info">
        Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }}
      </div>

      <div class="pagination-actions">
        <button class="page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
        <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
        <button class="page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
      </div>
    </div>

    <!-- Summary Modal -->
    <div v-if="showSummaryModal" class="modal-backdrop" @click.self="closeSummaryModal">
      <div class="modal-card reason-modal-card">
        <div class="modal-header">
          <h3 class="modal-title">Closing Package Summary</h3>
          <button class="close-btn" @click="closeSummaryModal">✕</button>
        </div>

        <div class="modal-body reason-modal-body">
          <div class="reason-meta">
            <div><strong>Loan #:</strong> {{ selectedRow.loannumber || selectedRow.loanid || '—' }}</div>
            <div><strong>Borrower:</strong> {{ borrowerName(selectedRow) }}</div>
            <div><strong>Status:</strong> {{ statusLabel(selectedRow.status) }}</div>
            <div><strong>Pages Scanned:</strong> {{ selectedRow.pagesscanned ?? 0 }}</div>
            <div><strong>Run:</strong> {{ selectedRow.dryrun ? 'Dry Run' : 'Live' }}</div>
          </div>

          <div v-if="docChips(selectedRow.foundlist).length" class="doc-chip-group">
            <div class="doc-chip-label">Found ({{ selectedRow.foundcount ?? 0 }})</div>
            <div class="doc-chips">
              <span v-for="d in docChips(selectedRow.foundlist)" :key="'f-' + d" class="doc-chip doc-chip--found">
                {{ d }}
              </span>
            </div>
          </div>

          <div v-if="docChips(selectedRow.missinglist).length" class="doc-chip-group">
            <div class="doc-chip-label">Not Found ({{ selectedRow.notfoundcount ?? 0 }})</div>
            <div class="doc-chips">
              <span v-for="d in docChips(selectedRow.missinglist)" :key="'m-' + d" class="doc-chip doc-chip--missing">
                {{ d }}
              </span>
            </div>
          </div>

          <div class="reason-content">
            {{ selectedRow.summary || '—' }}
          </div>

          <div v-if="selectedRow.error" class="reason-content" style="margin-top: 12px; border-color: #f0d1d5; background: #fff0f1;">
            <strong>Error:</strong> {{ selectedRow.error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/api.service'
import auth from "@/authConfig.js"

// Maps the parser's catalog keys (stored in FoundList/MissingList csv)
// to their friendly GI document titles.
const DOC_TITLES = {
  note: 'GI - Note',
  mortgage: 'GI - Mortgage & Riders',
  cd: 'GI - CD',
  final1003: 'GI - Final 1003',
  addendum92900a: 'GI - Combined 1003 Addendum - 92900A',
  warrantydeed: 'GI - Warranty Deed',
  borrowercert: 'GI - Borrower Certification and Authorization',
  federaldisclosures: 'GI - Federal Disclosures',
  hudappraisedvalue: 'GI - HUD Appraised Value Disclosure',
  settlementcert: 'GI - Settlement Certification'
}

export default {
  name: 'AiInsuringDashboard',
  data() {
    return {
      loading: false,
      errorMessage: '',
      rows: [],
      timeFrame: 'last_7_days',
      search: '',
      statusFilter: '',
      dryRunFilter: '',
      sortBy: 'processedatutc',
      sortDir: 'desc',
      page: 1,
      pageSize: 100,
      showSummaryModal: false,
      selectedRow: {}
    }
  },
  computed: {
    summary() {
      const rows = this.visibleRows || []

      return {
        total: rows.length,
        processed: rows.filter(x => x.status === 'processed').length,
        noPackage: rows.filter(x => x.status === 'no_package').length,
        failed: rows.filter(x => x.status === 'failed' || !!x.error).length,
        withMissing: rows.filter(x => (x.notfoundcount ?? 0) > 0 && x.status === 'processed').length
      }
    },
    visibleRows() {
      return (this.rows || []).filter(x => this.matchesTimeFrame(x?.processedatutc))
    },
    filteredRows() {
      let rows = [...this.visibleRows]

      if (this.search) {
        const term = this.search.toLowerCase()
        rows = rows.filter(x => {
          const haystack = [
            x.loanid,
            x.loannumber,
            x.borrowername,
            x.borrowerfirstname,
            x.borrowerlastname,
            x.loanofficername,
            x.orgid,
            x.status,
            x.foundlist,
            x.missinglist,
            x.summary,
            x.error
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

          return haystack.includes(term)
        })
      }

      if (this.statusFilter) {
        rows = rows.filter(x => x.status === this.statusFilter)
      }

      if (this.dryRunFilter === 'dry') {
        rows = rows.filter(x => !!x.dryrun)
      } else if (this.dryRunFilter === 'live') {
        rows = rows.filter(x => !x.dryrun)
      }

      rows.sort((a, b) => {
        const aVal = this.sortValue(a, this.sortBy)
        const bVal = this.sortValue(b, this.sortBy)

        if (aVal === bVal) return 0
        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
        }

        const result = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true,
          sensitivity: 'base'
        })

        return this.sortDir === 'asc' ? result : -result
      })

      return rows
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    pageStart() {
      if (!this.filteredRows.length) return 0
      return (this.page - 1) * this.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.page * this.pageSize, this.filteredRows.length)
    }
  },
  watch: {
    search() { this.page = 1 },
    statusFilter() { this.page = 1 },
    dryRunFilter() { this.page = 1 },
    timeFrame() { this.page = 1 },
    filteredRows() {
      if (this.page > this.totalPages) this.page = 1
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    getRequestDays() {
      if (this.timeFrame === 'today') return 2
      if (this.timeFrame === 'yesterday') return 2
      if (this.timeFrame === 'this_week') return 7
      if (this.timeFrame === 'last_week') return 14
      if (this.timeFrame === 'last_7_days') return 7
      if (this.timeFrame === 'last_30_days') return 30
      if (this.timeFrame === 'last_90_days') return 90
      return 7
    },
    startOfDay(date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    },
    endOfDay(date) {
      const d = new Date(date)
      d.setHours(23, 59, 59, 999)
      return d
    },
    startOfWeek(date) {
      const d = this.startOfDay(date)
      const day = d.getDay()
      const diff = day === 0 ? -6 : 1 - day
      d.setDate(d.getDate() + diff)
      return d
    },
    endOfWeek(date) {
      const d = this.startOfWeek(date)
      d.setDate(d.getDate() + 6)
      d.setHours(23, 59, 59, 999)
      return d
    },
    matchesTimeFrame(value) {
      if (!value) return false
      const rowDate = new Date(value)
      if (Number.isNaN(rowDate.getTime())) return false

      const now = new Date()
      const todayStart = this.startOfDay(now)
      const todayEnd = this.endOfDay(now)

      if (this.timeFrame === 'today') return rowDate >= todayStart && rowDate <= todayEnd

      if (this.timeFrame === 'yesterday') {
        const yesterday = new Date(todayStart)
        yesterday.setDate(yesterday.getDate() - 1)
        return rowDate >= this.startOfDay(yesterday) && rowDate <= this.endOfDay(yesterday)
      }

      if (this.timeFrame === 'this_week') return rowDate >= this.startOfWeek(now) && rowDate <= this.endOfWeek(now)

      if (this.timeFrame === 'last_week') {
        const thisWeekStart = this.startOfWeek(now)
        const lastWeekStart = new Date(thisWeekStart)
        lastWeekStart.setDate(lastWeekStart.getDate() - 7)
        const lastWeekEnd = new Date(thisWeekStart)
        lastWeekEnd.setMilliseconds(-1)
        return rowDate >= lastWeekStart && rowDate <= lastWeekEnd
      }

      if (this.timeFrame === 'last_7_days') {
        const start = this.startOfDay(now)
        start.setDate(start.getDate() - 6)
        return rowDate >= start && rowDate <= todayEnd
      }

      if (this.timeFrame === 'last_30_days') {
        const start = this.startOfDay(now)
        start.setDate(start.getDate() - 29)
        return rowDate >= start && rowDate <= todayEnd
      }

      if (this.timeFrame === 'last_90_days') {
        const start = this.startOfDay(now)
        start.setDate(start.getDate() - 89)
        return rowDate >= start && rowDate <= todayEnd
      }

      return true
    },
    async loadData() {
      this.loading = true
      this.errorMessage = ''

      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken

        const response = await api.GetAiInsuringLog(this.getRequestDays(), token)

        this.rows = response?.data?.data || []
      } catch (e) {
        this.rows = []
        this.errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load AI Insuring log.'
      } finally {
        this.loading = false
      }
    },
    borrowerName(row) {
      if (row?.borrowername) return row.borrowername
      const first = row?.borrowerfirstname || ''
      const last = row?.borrowerlastname || ''
      const full = `${first} ${last}`.trim()
      return full || '—'
    },
    statusLabel(status) {
      if (status === 'processed') return 'Processed'
      if (status === 'no_package') return 'No Package'
      if (status === 'failed') return 'Failed'
      return status || '—'
    },
    statusClass(status) {
      if (status === 'processed') return 'approve'
      if (status === 'no_package') return 'request-only'
      if (status === 'failed') return 'manual-review'
      return 'no-audit'
    },
    docChips(csv) {
      if (!csv) return []
      return String(csv)
        .split(',')
        .map(k => k.trim())
        .filter(Boolean)
        .map(k => DOC_TITLES[k.toLowerCase()] || k)
    },
    docListTitle(csv) {
      const chips = this.docChips(csv)
      return chips.length ? chips.join('\n') : ''
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString('en-US')
    },
    sortValue(row, key) {
      if (key === 'processedatutc') return new Date(row.processedatutc).getTime()
      if (key === 'dryrun') return row.dryrun ? 1 : 0
      if (key === 'foundcount') return Number(row.foundcount || 0)
      if (key === 'notfoundcount') return Number(row.notfoundcount || 0)
      if (key === 'pagesscanned') return Number(row.pagesscanned || 0)
      return row[key]
    },
    setSort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortBy = key
      this.sortDir = 'asc'
    },
    sortIcon(key) {
      if (this.sortBy !== key) return ''
      return this.sortDir === 'asc' ? '▲' : '▼'
    },
    prevPage() { if (this.page > 1) this.page-- },
    nextPage() { if (this.page < this.totalPages) this.page++ },
    openSummaryModal(row) {
      this.selectedRow = row || {}
      this.showSummaryModal = true
    },
    closeSummaryModal() {
      this.showSummaryModal = false
      this.selectedRow = {}
    }
  }
}
</script>

<style scoped>
.hmda-page {
  padding: 18px;
  background: #f7f8fb;
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  color: #1f2a44;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-icon {
  width: 48px;
  height: 48px;
  border-radius: 5px;
  background: #123a71;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex: 0 0 auto;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #7b8397;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.summary-sections {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr;
  gap: 12px;
  margin-bottom: 12px;
}

.summary-section {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  padding: 12px;
}

.decisions-section { background: #fcfbff; }

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon { color: #5f79c7; font-size: 14px; }
.section-title { font-size: 14px; font-weight: 700; color: #28344f; }

.section-cards { display: grid; gap: 10px; }
.summary-section:first-child .section-cards { grid-template-columns: 1fr; }
.decisions-section .results-cards { grid-template-columns: repeat(4, 1fr); }

.summary-card {
  background: #fdfdfe;
  border: 1px solid #e8ebf2;
  border-radius: 5px;
  padding: 12px;
}

.summary-card.approve { border-color: #d8eedf; background: #fbfefc; }
.summary-card.request-only { border-color: #f4e7c4; background: #fffdfa; }
.summary-card.manual-review { border-color: #f2d7d9; background: #fffafb; }
.summary-card.no-audit { border-color: #e8ebf2; background: #fbfbfd; }

.summary-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.summary-icon { font-size: 13px; color: #6a7b9f; }
.summary-label { font-size: 12px; color: #7b8397; font-weight: 600; }

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #1f2a44;
  line-height: 1.1;
}

.toolbar { margin-bottom: 10px; }

.single-line-toolbar {
  display: grid;
  grid-template-columns: 1fr 190px 170px;
  gap: 8px;
  align-items: center;
}

.search-input,
.filter-select {
  height: 38px;
  border: 1px solid #dfe4ee;
  background: #fcfcfe;
  border-radius: 5px;
  padding: 0 14px;
  font-size: 13px;
  width: 100%;
  color: #23314f;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.search-input::placeholder { color: #94a0b8; }

.search-input:focus,
.filter-select:focus {
  border-color: #cfd8e8;
  box-shadow: 0 0 0 2px rgba(49, 93, 181, 0.08);
}

.days-select { min-width: 140px; }

.refresh-btn,
.page-btn,
.close-btn,
.summary-btn {
  border: 1px solid #d9dfeb;
  border-radius: 5px;
  background: #ffffff;
  color: #22314f;
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.refresh-btn:hover,
.page-btn:hover,
.summary-btn:hover {
  background: #f4f7fc;
  border-color: #cfd8e8;
}

.summary-btn {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
  color: #315db5;
  background: #f5f8fc;
  border-color: #d9e2f0;
}

.refresh-btn:disabled,
.page-btn:disabled,
.summary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.table-card {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
}

.table-card-title-wrap { display: flex; align-items: center; gap: 8px; }
.table-card-icon { color: #5f79c7; font-size: 14px; }
.table-card-title { font-size: 14px; font-weight: 700; color: #28344f; }
.table-card-count { font-size: 13px; font-weight: 700; color: #7b8397; }

.table-wrap { overflow: auto; flex: 1; }

.locks-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1300px;
}

.locks-table th,
.locks-table td {
  padding: 12px;
  border-bottom: 1px solid #edf0f5;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
}

.locks-table th {
  background: #f7f9fc;
  color: #6f7b92;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  position: sticky;
  top: 0;
  z-index: 1;
}

.locks-table tbody tr { background: #fbfbfd; }
.locks-table tbody tr:hover { background: #f8faff; }

.primary-text { font-weight: 700; color: #24314f; }
.compact-cell { white-space: nowrap; }

.decision-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}

.decision-pill.approve { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.decision-pill.request-only { background: #fff7e7; border-color: #f5e3b5; color: #9a6b11; }
.decision-pill.manual-review { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.decision-pill.no-audit { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }

/* Found / missing count badges */
.count-good,
.count-bad,
.count-zero {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 800;
}
.count-good { background: #eef9f0; color: #2c7a4b; }
.count-bad  { background: #fff0f1; color: #b24553; }
.count-zero { background: #f2f4f7; color: #6d7789; }

.table-state { padding: 28px; text-align: center; color: #7b8397; font-size: 14px; }
.table-state.error { color: #b94b5c; }

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 4px 0;
  gap: 12px;
}

.pagination-info { color: #7b8397; font-size: 13px; font-weight: 500; }
.pagination-actions { display: flex; align-items: center; gap: 8px; }
.page-number { font-size: 13px; color: #5c6780; font-weight: 600; }

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 37, 56, 0.32);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000;
}

.modal-card {
  width: min(760px, 100%);
  max-height: 85vh;
  background: #fcfcfe;
  border: 1px solid #e7ebf2;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(28, 37, 56, 0.12);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
}

.modal-title { margin: 0; font-size: 16px; color: #22314f; font-weight: 700; }
.close-btn { width: 36px; height: 36px; padding: 0; }

.reason-modal-body { padding: 18px; overflow-y: auto; max-height: calc(85vh - 70px); }

.reason-meta {
  display: grid;
  gap: 6px;
  margin-bottom: 14px;
  color: #5f6b84;
  font-size: 13px;
}

/* Found / missing document chips in the modal */
.doc-chip-group { margin-bottom: 12px; }
.doc-chip-label { font-size: 12px; font-weight: 700; color: #6f7b92; margin-bottom: 6px; }
.doc-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.doc-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid transparent;
  white-space: nowrap;
}
.doc-chip--found { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.doc-chip--missing { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }

.reason-content {
  font-size: 13px;
  line-height: 1.65;
  color: #24314f;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f7f9fc;
  border: 1px solid #e6ebf2;
  border-radius: 5px;
  padding: 14px;
}

@media (max-width: 1600px) {
  .summary-sections { grid-template-columns: 1fr; }
}

@media (max-width: 1200px) {
  .single-line-toolbar { grid-template-columns: 1fr 1fr; }
  .decisions-section .results-cards { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 900px) {
  .page-header { flex-direction: column; align-items: stretch; }
  .header-actions { width: 100%; flex-wrap: wrap; }
}

@media (max-width: 700px) {
  .hmda-page { padding: 16px; }
  .single-line-toolbar { grid-template-columns: 1fr; }
  .summary-section:first-child .section-cards,
  .decisions-section .results-cards { grid-template-columns: 1fr; }
  .pagination-bar { flex-direction: column; align-items: flex-start; }
}
</style>
